import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { Suspense, useEffect, useState } from "react";

import { SiteIntegration, UserIdentity } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import Switch from "~/components/switch/Switch";
import Modal from "~/components/ui/modal/Modal";
import Spinner from "~/components/ui/spinner/Spinner";
import { useAuth } from "~/contexts/AuthProvider.tsx";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { IntegrationConfigurationPanel } from "../IntegrationConfigurationPanel/IntegrationConfigurationPanel";
import { IntegrationConfigurationPanelReadOnly } from "../IntegrationConfigurationPanel/IntegrationConfigurationPanelReadOnly";
import { IntegrationConfigurationPanelSkeleton } from "../IntegrationConfigurationPanel/IntegrationConfigurationPanelSkeleton";
import { SiteIntegrationWithAuthorizations } from "../withAuthorizations";
import IdentitySelectionModal from "./IdentitySelectionModal";
import styles from "./IntegrationContainer.module.scss";
import { useIdentitySelectionController } from "./useIdentitySelectionController";

interface IntegrationContainerProps {
  title: string;
  kind: SiteIntegration["kind"];
  integration: SiteIntegrationWithAuthorizations | undefined;
  siteId: number;
  identities: UserIdentity[];
}

export const IntegrationContainer = ({
  title,
  kind,
  integration,
  siteId,
  identities,
}: IntegrationContainerProps) => {
  const { user } = useAuth();

  const ownIdentities = identities.filter(
    (identity) => identity.user_id === user?.id,
  );

  const queryClient = useQueryClient();
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  const [isChecked, setChecked] = useState(Boolean(integration));

  useEffect(() => {
    setChecked(Boolean(integration));
  }, [integration]);

  const { mutateAsync: createSiteIntegration, isPending: isCreating } =
    useMutation({
      mutationFn: async (params: {
        kind: SiteIntegration["kind"];
        identity: UserIdentity;
      }) => {
        const response = await apiClient.sites.integrationsCreate(siteId, {
          kind: params.kind,
          user_identity_id: params.identity.id,
          properties: [],
        });

        await Promise.all([
          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
          }),
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.DASHBOARDS(siteId),
          }),
        ]);

        return response.data.site_integration;
      },
    });

  const { onRequestIdentitySelection, modalProps } =
    useIdentitySelectionController({
      identities: ownIdentities,
      onIdentitySelected: async (selectedIdentity) => {
        setChecked(true);
        const result = await tryCatch(
          createSiteIntegration({ kind, identity: selectedIdentity }),
        );

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });
          setChecked(false);
        }
      },
    });

  const { mutateAsync: deleteSiteIntegration, isPending: isDeleting } =
    useMutation({
      mutationFn: async (integrationId: number) => {
        const response = await apiClient.sites.integrationsDelete(
          siteId,
          integrationId,
        );

        await Promise.all([
          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
          }),
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.DASHBOARDS(siteId),
          }),
        ]);

        return response;
      },
    });

  const onToggleIntegration = async () => {
    if (integration) {
      // Show confirmation modal instead of performing action
      setIsDisconnectModalOpen(true);
    } else {
      onRequestIdentitySelection();
    }
  };

  const onConfirmDisconnect = async () => {
    if (!integration) return;

    setIsDisconnectModalOpen(false);

    setChecked(false);
    const result = await tryCatch(deleteSiteIntegration(integration.record.id));

    if (result.error) {
      enqueueSnackbar(messageFromError(result.error), {
        variant: "error",
        persist: true,
      });
      setChecked(true);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h4>{title}</h4>
        <Switch
          checked={isChecked}
          onChange={onToggleIntegration}
          disabled={
            isCreating ||
            isDeleting ||
            ownIdentities.length === 0 ||
            (integration && !integration.can.destroy)
          }
        />
        {(isCreating || isDeleting) && (
          <div>
            <Spinner size="1rem" />
          </div>
        )}
      </div>
      {integration &&
        (integration.can.configuration && integration.can.update ? (
          <Suspense fallback={<IntegrationConfigurationPanelSkeleton />}>
            <IntegrationConfigurationPanel
              siteId={siteId}
              integration={integration.record}
              identities={identities}
            />
          </Suspense>
        ) : (
          <IntegrationConfigurationPanelReadOnly
            integration={integration.record}
            identities={identities}
          />
        ))}
      <IdentitySelectionModal {...modalProps} />

      <Modal
        isOpen={isDisconnectModalOpen}
        modalLabel={<h3>Are you sure you want to disable this integration?</h3>}
        closeModal={() => setIsDisconnectModalOpen(false)}
      >
        <p>You can re-enable it at any time.</p>
        <div className={styles.modalActions}>
          <Button
            variant="outlined"
            onClick={() => setIsDisconnectModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirmDisconnect}
            loading={isDeleting}
          >
            Disable
          </Button>
        </div>
      </Modal>
    </div>
  );
};
